import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  forwardRef,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgControl } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ICON_ACTIONS, INTERNAL_ICONS, TRANSLATION_PREFIX } from '@klubovna/shared-models';
import { INPUT_TYPES } from '@klubovna/shared-models';
import { IconAction, InputValue } from '@klubovna/shared-models';

@Component({
  selector: 'k-input',
  imports: [ TranslateModule ],
  templateUrl: './input.component.html',
  styleUrl: './input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor, AfterViewInit {
  placeholder = input<string>('');
  type = input<string>('text');
  errorMessages = input<Record<string, string>>({});
  required = input<boolean>(false);
  suffixIcon = input<string>('');
  showClearButton = input<boolean>(false);

  value = signal<InputValue>('');
  disabled = signal(false);
  isError = signal(false);
  currentErrorMessage = signal<string | null>(null);
  isRequired = signal(false);
  passwordVisible = signal(false);

  displayPlaceholder = computed(() => {
    const baseText = this.placeholder();
    const required = this.isRequired();
    return required && baseText ? `${ baseText } *` : baseText;
  });

  effectiveType = computed(() => {
    const baseType = this.type();
    if (baseType === INPUT_TYPES.PASSWORD && this.passwordVisible()) {
      return INPUT_TYPES.TEXT;
    }
    return baseType;
  });

  shouldShowClear = computed(() => {
    return this.showClearButton() && !!this.value() && !this.disabled();
  });

  effectiveIcon = computed(() => {
    const baseType = this.type();
    const customIcon = this.suffixIcon();

    if (this.shouldShowClear()) {
      return INTERNAL_ICONS.CLOSE;
    }

    if (baseType === INPUT_TYPES.PASSWORD) {
      return this.passwordVisible() ? INTERNAL_ICONS.VISIBILITY_OFF : INTERNAL_ICONS.VISIBILITY;
    }

    return customIcon;
  });

  hasSuffixIcon = computed(() => !!this.effectiveIcon());
  isPasswordField = computed(() => this.type() === INPUT_TYPES.PASSWORD);
  isIconClickable = computed(() => this.isPasswordField() || this.shouldShowClear());

  iconAction = computed((): IconAction => {
    if (this.shouldShowClear()) {
      return ICON_ACTIONS.CLEAR;
    }
    if (this.isPasswordField()) {
      return ICON_ACTIONS.TOGGLE_PASSWORD;
    }
    return ICON_ACTIONS.NONE;
  });

  private ngControl = signal<NgControl | null>(null);
  private injector = inject(Injector);
  private destroyRef = inject(DestroyRef);

  constructor() {
    effect(() => {
      const control = this.ngControl()?.control;
      if (control) {
        this.updateErrorState(control);
      }
    });
  }

  ngAfterViewInit(): void {
    const ngControl = this.injector.get(NgControl, null);
    this.ngControl.set(ngControl);

    if (ngControl?.control) {
      this.updateRequiredState(ngControl.control);

      ngControl.control.statusChanges
        ?.pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.ngControl.set(this.ngControl());
        });
    }
  }

  private updateRequiredState(control: any): void {
    if (control && control.validator) {
      const validator = control.validator({} as any);
      const hasRequiredValidator = validator && validator.required;
      this.isRequired.set(hasRequiredValidator || this.required());
    } else {
      this.isRequired.set(this.required());
    }
  }

  private onChange = (value: InputValue) => {
  };
  private onTouched = () => {
  };

  writeValue(value: InputValue): void {
    this.value.set(value ?? '');
  }

  registerOnChange(fn: (value: InputValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const inputValue: InputValue =
      this.type() === INPUT_TYPES.NUMBER ? (target.value === '' ? '' : Number(target.value)) : target.value;

    this.value.set(inputValue);
    this.onChange(inputValue);

    if (this.isError()) {
      this.isError.set(false);
      this.currentErrorMessage.set(null);
    }
  }

  onBlur(): void {
    this.onTouched();

    setTimeout(() => {
      const control = this.ngControl()?.control;
      if (control) {
        this.updateErrorState(control);
      }
    }, 0);
  }

  onSuffixIconClick(): void {
    const action = this.iconAction();

    switch (action) {
      case ICON_ACTIONS.CLEAR:
        this.clearValue();
        break;
      case ICON_ACTIONS.TOGGLE_PASSWORD:
        this.passwordVisible.set(!this.passwordVisible());
        break;
      default:
        break;
    }
  }

  clearValue(): void {
    const emptyValue: InputValue = '';
    this.value.set(emptyValue);
    this.onChange(emptyValue);

    if (this.isError()) {
      this.isError.set(false);
      this.currentErrorMessage.set(null);
    }
  }

  private updateErrorState(control: any): void {
    const hasError = control.invalid && control.touched;
    this.isError.set(hasError);

    if (hasError && control.errors) {
      const errorMessage = this.getErrorMessage(control.errors);
      this.currentErrorMessage.set(errorMessage);
    } else {
      this.currentErrorMessage.set(null);
    }
  }

  private getErrorMessage(errors: any): string | null {
    const errorKeys = Object.keys(errors);
    const firstErrorKey = errorKeys[0];

    if (firstErrorKey && this.errorMessages()[firstErrorKey]) {
      const translationKey = this.errorMessages()[firstErrorKey];
      return `${ TRANSLATION_PREFIX }${ translationKey }`;
    }

    if (firstErrorKey) {
      return `${ TRANSLATION_PREFIX }${ firstErrorKey }`;
    }

    return null;
  }
}
